import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  builtinMarketplaceAdapters,
  evaluateCanonicalCompleteness,
  formatProviderReadinessLine,
  projectProviderReadiness,
  type CanonicalProductSnapshot,
} from "@/lib/marketplace-readiness";
import { formatAmazonCatalogueStatus, GOVERNED_MATERIAL_ATTRIBUTE, evaluateProductDetailsPreparation } from "@/lib/catalogue-facts";
import type { ProductRow } from "../types";

export default function ProductReadinessPanel(props: {
  orgId: string;
  product: ProductRow;
  hasSupplier: boolean;
  imageCount: number;
}) {
  const p = props.product;
  const { data } = useQuery({
    queryKey: ["product-readiness-ids", props.orgId, p.id],
    queryFn: async () => {
      const [ids, cons, media, attrs] = await Promise.all([
        supabase.from("product_identifiers").select("type, value").eq("org_id", props.orgId).eq("product_id", p.id),
        supabase.from("connectors").select("provider, auth_status").eq("org_id", props.orgId),
        supabase.from("product_media").select("id", { count: "exact", head: true }).eq("org_id", props.orgId).eq("product_id", p.id).eq("status", "active"),
        supabase.from("product_attribute_values").select("value, product_attributes(name)").eq("org_id", props.orgId).eq("product_id", p.id),
      ]);
      const material = ((attrs.data ?? []) as { value: string; product_attributes?: { name: string } | { name: string }[] | null }[])
        .find((row) => {
          const n = Array.isArray(row.product_attributes) ? row.product_attributes[0]?.name : row.product_attributes?.name;
          return (n ?? "").toLowerCase() === GOVERNED_MATERIAL_ATTRIBUTE.toLowerCase();
        })?.value ?? null;
      return {
        identifiers: (ids.data ?? []) as { type: string; value: string }[],
        connectors: (cons.data ?? []) as { provider: string; auth_status: string | null }[],
        imageCount: media.count ?? props.imageCount,
        material,
      };
    },
  });

  const ident = data?.identifiers ?? [];
  const snapshot: CanonicalProductSnapshot = {
    name: p.name,
    sku: p.sku,
    brand: p.brand,
    manufacturer: p.manufacturer,
    mpn: p.mpn,
    condition: p.condition,
    categoryId: p.category_id,
    gtin: ident.find((i) => i.type === "gtin")?.value ?? null,
    upc: ident.find((i) => i.type === "upc")?.value ?? null,
    ean: ident.find((i) => i.type === "ean")?.value ?? null,
    barcode: p.barcode,
    identifiersNotApplicable: Boolean(p.identifiers_not_applicable),
    weightKg: p.weight_kg,
    lengthCm: p.length_cm,
    widthCm: p.width_cm,
    heightCm: p.height_cm,
    description: p.description,
    shortDescription: p.short_description,
    imageCount: data?.imageCount ?? props.imageCount,
    sellingPrice: p.selling_price,
    hasSupplier: props.hasSupplier,
    countryOfOrigin: p.country_of_origin,
    material: data?.material ?? null,
  };
  const canonical = evaluateCanonicalCompleteness(snapshot);
  const connected = new Set(
    (data?.connectors ?? [])
      .filter((c) => c.auth_status === "connected" || c.auth_status === "authorized")
      .map((c) => c.provider),
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="product-readiness">
      <h3 className="text-sm font-semibold text-slate-800">Product readiness</h3>
      <p className="text-sm text-slate-700 mt-1">{canonical.summary}</p>
      <p className="text-xs text-slate-500 mt-1">
        Recommended catalogue facts are not Amazon requirements. A missing GTIN is not Amazon exemption.
      </p>
      {canonical.gaps.length > 0 && (
        <ul className="mt-2 space-y-1">
          {canonical.gaps.map((g) => (
            <li key={g.field} className="text-xs text-slate-500">
              Missing {g.label} — recommended, not required to keep this product in Tradivo
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
        {builtinMarketplaceAdapters.map((adapter) => {
          if (adapter.provider === "amazon") {
            const details = evaluateProductDetailsPreparation({
              name: snapshot.name,
              brand: snapshot.brand,
              gtin: snapshot.gtin,
              upc: snapshot.upc,
              ean: snapshot.ean,
              identifiersNotApplicable: snapshot.identifiersNotApplicable,
              condition: snapshot.condition,
              mpn: snapshot.mpn,
              weightKg: snapshot.weightKg,
              lengthCm: snapshot.lengthCm,
              widthCm: snapshot.widthCm,
              heightCm: snapshot.heightCm,
              description: snapshot.description,
              imageCount: snapshot.imageCount,
            });
            return (
              <p key={adapter.provider} className="text-xs text-slate-600" data-testid="amazon-catalogue-status">
                {formatAmazonCatalogueStatus(details)}
              </p>
            );
          }
          const row = projectProviderReadiness({
            adapter,
            product: snapshot,
            connected: connected.has(adapter.provider),
            evaluated: adapter.provider === "temu" ? false : true,
          });
          return (
            <p key={adapter.provider} className="text-xs text-slate-600">
              {formatProviderReadinessLine(row)}
            </p>
          );
        })}
      </div>
    </div>
  );
}
